import { classFeature } from "./mapping";
import type { Box, Detection, ObjectKind, ObjectTrack, TrackerResult, VisualEncounter } from "./types";

interface Track extends ObjectTrack { encounterId: string | null }
export interface TrackerOptions { threshold: number; minimumHits?: number; confirmationMs?: number; releaseMs?: number; reidentifyMs?: number }

export function intersectionOverUnion(a: Box, b: Box): number {
    const intersection = Math.max(0, Math.min(a[0] + a[2], b[0] + b[2]) - Math.max(a[0], b[0])) *
        Math.max(0, Math.min(a[1] + a[3], b[1] + b[3]) - Math.max(a[1], b[1]));
    return intersection / Math.max(1e-9, a[2] * a[3] + b[2] * b[3] - intersection);
}

/** Conservative encounter tracker: simultaneous/reappearing signal heads are
 * one visual episode, not independent proof of movement past several anchors. */
export class EncounterTracker {
    private tracks = new Map<string, Track>();
    private episodes = new Map<ObjectKind, { id: string; lastSeenMs: number }>();
    private nextTrack = 0;
    private nextEncounter = 0;
    private timestamp = -1;

    constructor(private sessionId: string, private options: TrackerOptions) {
        if (!Number.isFinite(options.threshold) || options.threshold < 0.25 || options.threshold > 0.95) throw new Error("Некоректний поріг детектора.");
    }

    update(detections: Detection[], timestampMs: number): TrackerResult {
        if (!Number.isFinite(timestampMs) || timestampMs < 0 || timestampMs < this.timestamp) throw new Error("Після перемотування потрібна нова сесія трекера.");
        if (timestampMs === this.timestamp) return { tracks: this.visible(timestampMs), encounters: [] };
        const previousFrame = this.timestamp;
        this.timestamp = timestampMs;
        const reidentifyMs = this.options.reidentifyMs ?? 20000;
        const releaseMs = this.options.releaseMs ?? 6000;
        for (const [id, track] of this.tracks) if (timestampMs - track.lastSeenMs > reidentifyMs) this.tracks.delete(id);
        for (const [kind, episode] of this.episodes) if (timestampMs - episode.lastSeenMs > releaseMs) this.episodes.delete(kind);
        const accepted = detections.filter(detection => detection.score >= this.options.threshold && Number.isFinite(detection.score) && detection.score <= 1 &&
            detection.box.every(Number.isFinite) && detection.box[2] > 0 && detection.box[3] > 0 && ["traffic_light", "stop_sign"].includes(detection.kind)).slice(0, 50);
        const pairs: { track: Track; detection: number; affinity: number }[] = [];
        for (const track of this.tracks.values()) accepted.forEach((detection, index) => {
            if (track.kind !== detection.kind) return;
            const overlap = intersectionOverUnion(track.box, detection.box);
            const center = Math.hypot(track.box[0] + track.box[2] / 2 - detection.box[0] - detection.box[2] / 2,
                track.box[1] + track.box[3] / 2 - detection.box[1] - detection.box[3] / 2);
            const sizeRatio = Math.sqrt(detection.box[2] * detection.box[3] / (track.box[2] * track.box[3]));
            const tolerance = Math.max(0.05, Math.min(0.16, (track.box[2] + track.box[3]) / 2));
            if (sizeRatio >= 0.3 && sizeRatio <= 3.3 && (overlap >= 0.08 || center <= tolerance)) {
                pairs.push({ track, detection: index, affinity: overlap + Math.max(0, 1 - center / tolerance) * 0.4 });
            }
        });
        pairs.sort((a, b) => b.affinity - a.affinity);
        const usedTracks = new Set<string>(), usedDetections = new Set<number>();
        for (const pair of pairs) {
            if (usedTracks.has(pair.track.id) || usedDetections.has(pair.detection)) continue;
            const track = pair.track, detection = accepted[pair.detection];
            if (!track.confirmed && track.lastSeenMs !== previousFrame && timestampMs - track.lastSeenMs > 1500) { track.hits = 0; track.firstSeenMs = timestampMs; }
            track.hits++; track.lastSeenMs = timestampMs;
            track.score = track.score * 0.6 + detection.score * 0.4;
            track.box = detection.box.map((value, index) => value * 0.8 + track.box[index] * 0.2) as Box;
            track.confirmed ||= track.hits >= (this.options.minimumHits ?? 3) && timestampMs - track.firstSeenMs >= (this.options.confirmationMs ?? 700);
            usedTracks.add(track.id); usedDetections.add(pair.detection);
        }
        accepted.forEach((detection, index) => {
            if (usedDetections.has(index)) return;
            const id = `${this.sessionId}:track-${++this.nextTrack}`;
            this.tracks.set(id, { ...detection, id, firstSeenMs: timestampMs, lastSeenMs: timestampMs, hits: 1, confirmed: false, encounterId: null });
        });
        const encounters: VisualEncounter[] = [];
        for (const kind of ["traffic_light", "stop_sign"] as const) {
            const visible = [...this.tracks.values()].filter(track => track.kind === kind && track.lastSeenMs === timestampMs);
            if (!visible.length) continue;
            let episode = this.episodes.get(kind);
            if (episode) episode.lastSeenMs = timestampMs;
            const confirmed = visible.filter(track => track.confirmed);
            if (!confirmed.length) continue;
            if (!episode) {
                const previous = confirmed.find(track => track.encounterId)?.encounterId;
                episode = { id: previous ?? `${this.sessionId}:event-${++this.nextEncounter}`, lastSeenMs: timestampMs };
                this.episodes.set(kind, episode);
                if (!previous) encounters.push({ id: episode.id, kind, timestampMs, confidence: Math.max(...confirmed.map(track => track.score)),
                    trackIds: confirmed.map(track => track.id), feature: classFeature(kind) });
            }
            for (const track of confirmed) track.encounterId = episode.id;
        }
        // Old, invisible tombstones are bounded even in long recordings.
        if (this.tracks.size > 128) {
            const oldest = [...this.tracks.values()].sort((a, b) => a.lastSeenMs - b.lastSeenMs);
            for (const track of oldest.slice(0, this.tracks.size - 128)) this.tracks.delete(track.id);
        }
        return { tracks: this.visible(timestampMs), encounters };
    }

    private visible(timestampMs: number): ObjectTrack[] {
        return [...this.tracks.values()].filter(track => track.lastSeenMs === timestampMs).map(track => ({
            id: track.id, kind: track.kind, score: track.score, box: track.box, hits: track.hits,
            firstSeenMs: track.firstSeenMs, lastSeenMs: track.lastSeenMs, confirmed: track.confirmed,
        }));
    }
}
